function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return; var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import { createContext, useContext, useEffect, useRef, useMemo } from 'react';
import { useConst, usePrevious, useAutoControlled, useConstCallback } from '@bydesign-ui/hooks';
export function useTabs(options) {
  var optionsActive = options.active,
      onActiveChange = options.onActiveChange,
      defaultActive = options.defaultActive,
      controlled = options.controlled,
      shouldChangeActive = options.shouldChangeActive,
      onCloseItem = options.onCloseItem,
      onAddItem = options.onAddItem,
      _options$lazy = options.lazy,
      lazy = _options$lazy === void 0 ? false : _options$lazy,
      tabPosition = options.tabPosition;
  var preOptionsActive = usePrevious(optionsActive);

  var _useAutoControlled = useAutoControlled({
    value: optionsActive,
    onChange: onActiveChange,
    defaultValue: defaultActive,
    controlled: controlled !== null && controlled !== void 0 ? controlled : false
  }),
      _useAutoControlled2 = _slicedToArray(_useAutoControlled, 2),
      innerActive = _useAutoControlled2[0],
      setActive = _useAutoControlled2[1];

  var active = useMemo(function () {
    if (controlled === undefined && optionsActive && optionsActive !== preOptionsActive) {
      return optionsActive;
    }

    return innerActive;
  }, [optionsActive, innerActive, controlled]);
  var items = useConst(function () {
    return new Map();
  });
  var status = useRef(null);

  var closeItem = function closeItem(id) {
    if (active === id) {
      status.current = 'close';
    }

    onCloseItem === null || onCloseItem === void 0 ? void 0 : onCloseItem(id);
  };

  var addItem = function addItem() {
    status.current = 'add';
    onAddItem === null || onAddItem === void 0 ? void 0 : onAddItem();
  };

  var registerItem = useConstCallback(function (item) {
    var tabItem = items.get(item.id);

    if (tabItem) {
      if (tabItem.registerComplete) {
        console.error("Error: The TabItem.id '".concat(item.id, "' is not unique."));
        return;
      }

      tabItem.registerComplete = true;
    } else {
      items.set(item.id, item);
    }

    if (status.current === 'add') {
      setActive(item.id);
      status.current = null;
    }
  });
  var unregisterItem = useConstCallback(function (id) {
    if (status.current === 'close') {
      var tabIds = Array.from(items.keys());
      var indexToClose = tabIds.findIndex(function (tabId) {
        return tabId === id;
      });
      var indexToActive = indexToClose === tabIds.length - 1 ? indexToClose - 1 : indexToClose + 1;
      indexToActive > -1 && setActive(tabIds[indexToActive]);
      status.current = null;
    }

    items.delete(id);
  });
  var attachTabBarRef = useConstCallback(function (tabId, tabBarRef) {
    var tabItem = items.get(tabId);

    if (tabItem) {
      if (tabItem.registerComplete) {
        console.error("Error: The tab '".concat(tabId, "' has already registered."));
        return;
      }

      tabItem.tabBarRef = tabBarRef;
      tabItem.registerComplete = true;
    } else {
      items.set(tabId, {
        id: tabId,
        tabBarRef: tabBarRef
      });
    }
  });
  var removeTabBar = useConstCallback(function (tabId) {
    var tabItem = items.get(tabId);

    if (tabItem) {
      tabItem.registerComplete = false;
    }
  });
  return {
    active: active,
    setActive: setActive,
    items: items,
    closeItem: closeItem,
    addItem: addItem,
    registerItem: registerItem,
    unregisterItem: unregisterItem,
    lazy: lazy,
    shouldChangeActive: shouldChangeActive,
    attachTabBarRef: attachTabBarRef,
    tabPosition: tabPosition,
    removeTabBar: removeTabBar
  };
}

var noop = function noop() {};

export var TabsContext = createContext({
  active: '',
  setActive: noop,
  items: new Map(),
  closeItem: noop,
  addItem: noop,
  registerItem: noop,
  unregisterItem: noop,
  lazy: false,
  attachTabBarRef: noop,
  tabPosition: 'top',
  removeTabBar: noop
});
export var TabsContextProvider = TabsContext.Provider;
export var useTabsContext = function useTabsContext() {
  return useContext(TabsContext);
};
export function useTabItem(options) {
  var label = options.label,
      optionsId = options.id;
  var id = useConst(optionsId);
  var tabItem = useConst({
    label: '',
    id: id
  });
  tabItem.label = label;
  var tabs = useTabsContext();
  var active = tabs.active,
      closeItem = tabs.closeItem,
      registerItem = tabs.registerItem,
      unregisterItem = tabs.unregisterItem,
      lazy = tabs.lazy;
  useEffect(function () {
    registerItem(tabItem);
    return function () {
      return unregisterItem(id);
    };
  }, []);
  var isActive = active === id;
  var close = useConstCallback(function () {
    return closeItem(id);
  });
  return {
    isActive: isActive,
    close: close,
    lazy: lazy
  };
}
export function useTabBarItem(tabId, collapsed) {
  var tabBarRef = useRef(null);
  var tabs = useTabsContext();
  useEffect(function () {
    !collapsed && tabs.attachTabBarRef(tabId, tabBarRef);
    return function () {
      tabs.removeTabBar(tabId);
    };
  }, []);
  return {
    tabBarRef: tabBarRef
  };
}